import type { SpaceGits } from "@indietabletop/types";
import { EditMode } from "./EditMode.ts";
import type { Gang, GangModePermissions } from "./Gang.ts";

export class GangMode {
  gang: Gang;

  constructor(gang: Gang) {
    this.gang = gang;
  }

  get data() {
    return this.gang.data.mode ?? { type: "A_LA_CARTE" };
  }

  get type() {
    return this.data.type;
  }

  get isCheatModeActive() {
    return this.data.isCheatModeActive;
  }

  get runCount() {
    return "runCount" in this.data ? this.data.runCount : 0;
  }

  /**
   * Is this gang's mode ready to be wrapped up?
   *
   * Only really makes sense in Chef's Recommendation mode. In all other cases
   * will report true.
   */
  get hasBeenRun() {
    return this.type === "CHEFS_REC" ? this.runCount > 0 : true;
  }

  /**
   * In random build modes, a user can indicate that they are done list
   * building, without leaving their selected build mode.
   */
  get isDone() {
    return "isDone" in this.data ? (this.data.isDone ?? false) : false;
  }

  get permissions(): GangModePermissions {
    if (this.isCheatModeActive) {
      return {
        models: EditMode.ANY,
        weapons: EditMode.ANY,
        gear: EditMode.ANY,
        treatments: EditMode.ANY,
        mods: EditMode.ANY,
        repairs: EditMode.ANY,
      };
    }

    switch (this.type) {
      case "A_LA_CARTE": {
        return {
          models: EditMode.ANY,
          weapons: EditMode.ANY,
          gear: EditMode.ANY,
          treatments: EditMode.REMOVE,
          mods: EditMode.ANY,
          repairs: EditMode.REMOVE,
        };
      }

      case "CHEFS_REC": {
        return {
          models: EditMode.NONE,
          weapons: this.runCount > 0 ? EditMode.ANY : EditMode.NONE,
          gear: EditMode.NONE,
          treatments: EditMode.NONE,
          mods: EditMode.NONE,
          repairs: EditMode.NONE,
        };
      }

      case "CHAOS_MODE": {
        return {
          models: EditMode.NONE,
          weapons: EditMode.ANY,
          gear: EditMode.NONE,
          treatments: EditMode.NONE,
          mods: EditMode.NONE,
          repairs: EditMode.NONE,
        };
      }

      case "CAMPAIGN": {
        return {
          models: EditMode.ADD,
          weapons: EditMode.ADD | EditMode.CHANGE | EditMode.REMOVE_DIRECTLY,
          gear: EditMode.ADD | EditMode.REMOVE_DIRECTLY,
          treatments: EditMode.ADD,
          mods: EditMode.ADD | EditMode.REMOVE_DIRECTLY,
          repairs: EditMode.ADD,
        };
      }
    }
  }

  get isManualBuildMode() {
    return this.type === "A_LA_CARTE";
  }

  assertRandomBuildMode(): asserts this is this & {
    type: "CHAOS_MODE" | "CHEFS_REC";
  } {
    if (!this.isRandomBuildMode) {
      throw new Error(`Gang Mode not in random build mode.`);
    }
  }

  get isRandomBuildMode() {
    return this.type === "CHEFS_REC" || this.type === "CHAOS_MODE";
  }

  get isCampaignMode() {
    return this.type === "CAMPAIGN";
  }

  get isBuildMode() {
    return !this.isCampaignMode;
  }

  get capUsageStyle() {
    return this.type === "CHEFS_REC"
      ? "BASE_COST"
      : this.type === "A_LA_CARTE"
        ? "FULL_COST"
        : "NONE";
  }

  get itemCostVisibility() {
    return this.isManualBuildMode ? "VISIBLE" : "HIDDEN";
  }

  setCheatModeActive(isCheatModeActive: boolean) {
    return this.clone({ isCheatModeActive });
  }

  setIsDone() {
    this.assertRandomBuildMode();

    const nextGang = this.gang.clone({
      mode: { type: this.type, runCount: this.runCount, isDone: true },
    });

    return nextGang.mode;
  }

  switchToCampaign() {
    const { gang } = this;

    const initialStash = this.isManualBuildMode
      ? Math.max(gang.ruleset.constants.aLaCarteCapsBudget - gang.cost, 0)
      : 0;

    return gang.clone({ mode: { type: "CAMPAIGN", initialStash } }).mode;
  }

  clone(
    /**
     * New data to pass to the cloned value.
     *
     * isCheatModeActive is the only property that can be chaged without
     * introducing type issues.
     */
    data?: Partial<Pick<SpaceGits.GangModeData, "isCheatModeActive">>,
  ) {
    const clone = this.gang.clone({ mode: { ...this.data, ...data } });
    return clone.mode;
  }

  static initData(
    type: SpaceGits.GangModeData["type"],
  ): SpaceGits.GangModeData {
    if (type === "CHEFS_REC" || type === "CHAOS_MODE") {
      return { type, runCount: 0, isCheatModeActive: false };
    }

    if (type === "CAMPAIGN") {
      return { type, initialStash: 0 };
    }

    return { type };
  }
}
